import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { Button, theme } from '@theguarantors/tg-package-material-ui';
import { Fragment } from 'react';
import { useFormState, type FieldErrors } from 'react-hook-form';

import { Tooltip } from '@/shared/components/Tooltip';

import { useInviteEligibilityCheck } from './hooks/use-invite-eligibility-check';
import { useInviteFormRuntime } from './invite-lease-details.runtime-context';
import { INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE } from './utils/submit-errors.util';

type ErrorVisitResult = { hasOther: boolean; hasEligibility: boolean };

/** Walk RHF errors: real validation vs visual-only eligibility highlights. */
const classifyFormErrors = (errors: FieldErrors): ErrorVisitResult => {
  const result: ErrorVisitResult = { hasOther: false, hasEligibility: false };

  const visit = (value: unknown): void => {
    if (!value || typeof value !== 'object') {
      return;
    }
    if ('type' in value || 'message' in value) {
      const type = (value as { type?: string }).type;
      if (type === INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
        result.hasEligibility = true;
      } else {
        result.hasOther = true;
      }
      return;
    }
    Object.values(value as Record<string, unknown>).forEach(visit);
  };

  visit(errors);
  return result;
};

// The sticky "Send Invite" footer, rendered inside <LeaseDetailsForm>'s form via
// `ui.slots.bottom` (the engine's own actions are hidden via
// `ui.hideDefaultActions`). `type="submit"` drives the engine's handleSubmit.
// Markup mirrors the legacy InviteLeaseDetailsDrawerForm footer for parity.
export function InviteLeaseDetailsSharedFooter() {
  // Scoped subscription: only the footer re-renders when validity/submitting flips.
  const { errors, isValid, isSubmitting } = useFormState();
  const { errorMsg } = useInviteFormRuntime();
  const { hasBlock, isFeatureEnabled } = useInviteEligibilityCheck();

  // Warning/block set a zero-width RHF error for the red email border, which makes
  // `isValid` false. Warnings must still allow Send; block is gated by `hasBlock`.
  // Keep `isValid` for the empty-mount case — `errors` is {} until fields validate.
  const { hasOther, hasEligibility } = classifyFormErrors(errors);
  const invalidOnlyFromEligibility = !isValid && !hasOther && hasEligibility;
  const formIncomplete = !isValid && !invalidOnlyFromEligibility;
  const submitDisabled = isSubmitting || formIncomplete || (isFeatureEnabled && hasBlock);

  return (
    <Fragment>
      {errorMsg ? (
        <Alert severity="error" sx={{ marginY: 2 }}>
          {errorMsg}
        </Alert>
      ) : null}
      <Box
        sx={{
          position: 'sticky',
          bottom: -1,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${theme.palette.neutralGrey[200]}`,
          pt: 2,
          pb: 2,
          mt: 2,
          zIndex: 100,
          mx: -4,
          px: 4,
        }}
      >
        <Tooltip title={formIncomplete ? 'Please complete the required fields to proceed' : ''}>
          <Box>
            <Button
              variant="contained"
              type="submit"
              disabled={submitDisabled}
              loading={isSubmitting}
              id="RenterInviteSubmitButton"
              style={submitDisabled ? { pointerEvents: 'none' } : {}}
              sx={{ width: '100%', height: '55px' }}
            >
              Send Invite
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Fragment>
  );
}
